import { useNavigate } from "@tanstack/react-router";
import { toast } from "react-hot-toast";

import { TOASTER_ID_KEYS } from "@/constants";
import { useSession } from "@/hooks";
import { authSliceActions, PickAuthEndPoint } from "@/store";
import { useAppDispatch } from "@/store/hooks";
import { mapRole } from "@/utils/mapRole";

import { Role } from "../../_shared";
import { authApi } from "./auth.api";

type Auth = PickAuthEndPoint<"login">;
function useAuth() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id, role } = useSession();
  const [login, { data, isSuccess, isError, isLoading, error }] = useLoginMutation();

  const SignIn = async (body: Auth["args"], options?: App.Base.CallbackOptions<Auth["result"]>) => {
    try {
      const { data, error } = await login({
        email: body.email,
        password: body.password,
        company: body.company ? { id: body.company?.id } : null,
        recaptcha_token: body.recaptcha_token
      });
      const companyIdFromSession = sessionStorage.getItem("@App:temporaryCompanyId")!;

      // Preferir dados da resposta recém-obtida para evitar race com a store
      const responseUserId = String(data?.id ?? id ?? "");

      if (!error) options?.onSuccess?.(data);

      const effectiveRole = mapRole(data?.role as string) ?? role ?? null;

      const matchRoles: Role[] = [Role.Administrador, Role.Usuario, Role.Transportadora];
      if (!effectiveRole || !matchRoles.includes(effectiveRole)) return toast.error("Você não tem permissão de acesso!", { toasterId: TOASTER_ID_KEYS.AUTH.NOT_ALLOWED });

      let navigated = false;
      switch (effectiveRole) {
        case Role.Administrador:
        case Role.Usuario:
          navigate({ to: "/dashboard" });
          navigated = true;
          break;

        case Role.Transportadora:
          if (companyIdFromSession && responseUserId) {
            navigate({
              to: `/aprovar-termo/transportadora/empresa/$companyId/$userId`,
              params: { companyId: companyIdFromSession, userId: responseUserId }
            });
            navigated = true;
          } else if (responseUserId) {
            //TODO Implementar exibição de dashboard na visão da transportadora
            navigate({ to: "/dashboard" });
            navigated = true;
          } else {
            toast.error("Dados de usuário incompletos");
          }
          break;

        default:
          toast.error("Cargo não reconhecido");
      }

      // Fallback: se por algum motivo não navegou, vai para dashboard
      if (!navigated) {
        navigate({ to: "/dashboard" });
      }
    } catch (err: any) {
      options?.onError?.(err);
      if ((err?.response?.data?.message as string).includes("Usuário bloqueado")) {
        toast.error(err.response.data.message, { duration: 10000 });
      } /* else {
        toast.error("Usuário ou senha inválido");
      } */
    } finally {
      options?.onFinally?.();
    }

    return { data, isSuccess, isError, isLoading, error };
  };

  const SignOut = () => {
    dispatch(authSliceActions.logout());
    navigate({ to: "/login" });
  };

  return {
    SignIn,
    SignOut
  };
}

export const { useLoginMutation } = authApi;
export { useAuth };
